import _ from 'lodash'
import upperFirst from 'lodash/upperFirst'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'temporal-polyfill/global'

import { HopOnVehicle_StatusEnum } from 'src/api/gen/api'
import { enumsTranslationKeys, withFormattedCount } from 'src/commons/i18nEnums'
import { ORDERED_HOPON_VEHICLE_STATUS } from 'src/commons/orderedList'
import { Can } from 'src/commons/permissions'
import { ITemporalRange } from 'src/commons/temporalHelpers'
import CheckboxListFilter from 'src/muiv5/components/commons/SidePanel/Filters/CheckboxListFilter/CheckboxListFilter'
import TimeRangeFilterPro from 'src/muiv5/components/commons/SidePanel/Filters/TimeRangeFilter/TimeRangeFilterPro'
import { SidePanel } from 'src/muiv5/components/commons/SidePanel/SidePanel'

export interface IHopOnFilters {
  dateRange: ITemporalRange
  status: Set<HopOnVehicle_StatusEnum>
  zones: string[]
}

interface IProps {
  areaLabels: string[]
  defaultFilters: IHopOnFilters
  initialFilters: IHopOnFilters
  vehiclesCount: number
}

export default forwardRef(function (props: IProps, ref) {
  const { t } = useTranslation()

  const [filters, setFilters] = useState(props.initialFilters)
  useImperativeHandle(ref, () => filters, [filters])

  function onFilterChange(key: string, newValue: any) {
    return setFilters({ ...filters, [key]: newValue })
  }

  function onBothChanged(valueFrom?: Temporal.ZonedDateTime, valueTo?: Temporal.ZonedDateTime) {
    onFilterChange('dateRange', { from: valueFrom, to: valueTo })
  }

  function onFromChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('dateRange', { to: filters.dateRange.to, from: value })
  }

  function onToChanged(value?: Temporal.ZonedDateTime) {
    onFilterChange('dateRange', { to: value, from: filters.dateRange.from })
  }

  return (
    <SidePanel
      headerProps={{
        title: t('filtersPanel.title.default'),
        information: t('filtersPanel.count.hopon', withFormattedCount(props.vehiclesCount)),
        onReset: () => setFilters(props.defaultFilters),
      }}
    >
      <TimeRangeFilterPro
        filterId={'dateRange'}
        format="L LT"
        values={{
          from: filters.dateRange.from,
          to: filters.dateRange.to,
        }}
        title={upperFirst(t('time range'))}
        onBothChanged={onBothChanged}
        onFromChanged={onFromChanged}
        onToChanged={onToChanged}
        disableFuture
        normalize
        defaultExpanded
      />
      <CheckboxListFilter
        filterId={'status'}
        items={ORDERED_HOPON_VEHICLE_STATUS.map((key) => ({
          id: key,
          label: upperFirst(t(enumsTranslationKeys.HopOnVehicleStatus[key])),
        }))}
        values={_.map(filters['status'], (elem) => elem.toString())}
        title={upperFirst(t('hopon.filters.status.title'))}
        onFilterChange={(filterId: string, values: string[]) => {
          onFilterChange(
            filterId,
            values.map((value) => value as HopOnVehicle_StatusEnum)
          )
        }}
        displayCheckbox
      />
      <Can I="read" an="area">
        {props.areaLabels && (
          <CheckboxListFilter
            filterId={'zones'}
            title={upperFirst(t('vehicles.zones'))}
            items={props.areaLabels.map((area) => ({ id: area, label: area }))}
            values={filters['zones']}
            onFilterChange={onFilterChange}
            displayCheckbox
            unique
          />
        )}
      </Can>
    </SidePanel>
  )
})
